import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { createComponentWithRef, useSlot, SlotProvider } from '@nettbureau/core';
import { unwrapEmotionElement } from '@nettbureau/styling';
import { FocusableProvider } from '@react-aria/focus';
import { Children, isValidElement } from 'react';
import { TooltipProvider } from './tooltip-context.js';
import { useTooltipTrigger } from './use-tooltip-trigger.js';

/**
 * A component that serves as a control for opening a tooltip. Becomes active when a trigger element
 * is hovered or focused.
 */ const TooltipTrigger = createComponentWithRef((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('TooltipTrigger', props, ref);
    const { children } = mergedProps;
    const [triggerElement, tooltipElement] = Children.toArray(children);
    if (!/*#__PURE__*/ isValidElement(triggerElement) || !/*#__PURE__*/ isValidElement(tooltipElement)) {
        throw new Error('<TooltipTrigger> must have exactly two children: a trigger and a <Tooltip>.');
    }
    const [state, { triggerProps, tooltipProps }] = useTooltipTrigger(mergedProps, mergedRef);
    const overrides = {
        Tooltip: {
            ...tooltipProps,
            anchorRef: mergedRef
        }
    };
    return /*#__PURE__*/ jsxs(TooltipProvider, {
        value: state,
        children: [
            /*#__PURE__*/ jsx(FocusableProvider, {
                ...triggerProps,
                ref: mergedRef,
                children: triggerElement
            }),
            /*#__PURE__*/ jsx(SlotProvider, {
                value: overrides,
                children: tooltipElement
            })
        ]
    });
});
/**
 * This special generator function is used internally by React Aria to add metadata to the
 * collection node.
 */ TooltipTrigger.getCollectionNode = function* getCollectionNode(props) {
    const [trigger, content] = props.children;
    yield {
        element: unwrapEmotionElement(trigger),
        wrapper: (element)=>{
            return /*#__PURE__*/ jsxs(TooltipTrigger, {
                ...props,
                children: [
                    element,
                    content
                ]
            }, element.key);
        }
    };
};

export { TooltipTrigger };
//# sourceMappingURL=tooltip-trigger.js.map
